import { ScriptableContext } from 'chart.js'
import { Line } from 'react-chartjs-2/dist'
import moment, { Moment } from 'moment'
import { graphMonthWithYearPicker } from '../../helpers'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'

interface IGraphUnit {
    period: string
    volume: number
}

interface IGraph {
    stats: IGraphUnit[]
    period: [Moment, Moment]
    smallScreen?: boolean
}

const options = (data: any, smallScreen = false) => {
    const tickFontSize = smallScreen ? 11 : 14
    const values = data?.map((el: IGraphUnit) => Math.round(el.volume / 1000)) || []

    const maxValue = values.length ? Math.max(...values) : 100000
    const cleanMaxValue = (Math.floor(maxValue / 1000) + 1) * 1000
    const stepSize = cleanMaxValue / 5
    const max = cleanMaxValue + stepSize
    return {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
        },
        scales: {
            y: {
                position: 'right',
                grid: {
                    color: '#E6F1FF',
                },
                min: 0,
                max,
                ticks: {
                    color: '#8C8C8C',
                    font: {
                        size: tickFontSize,
                        weight: '400',
                    },
                    stepSize,
                },
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#8C8C8C',
                    font: {
                        size: tickFontSize,
                        weight: '400',
                    },
                },
            },
        },
    }
}

const generateMonths = (period: [Moment, Moment] | null) => {
    if (!period) {
        return []
    }
    const start = moment(period[0], 'YYYY-MM-DD')
    const end = moment(period[1], 'YYYY-MM-DD')

    if (end.date() === 1) {
        end.add(1, 'day')
    }

    const months = []
    const current = start

    while (current.isSameOrBefore(end)) {
        months.push(current.format('YYYY-MM-DD'))
        current.add(1, 'month')
    }

    return months
}

const generateData = (list: any, locale: string, period?: [Moment, Moment]) => {
    let labelsGenerated = []
    let data = []
    if (period) {
        const monthGeneratedList = generateMonths(period)
        labelsGenerated = monthGeneratedList
        data = Array(monthGeneratedList.length).fill(0)
    } else {
        // GENERATE AND FILL EMPTY DATA
        data = Array(6).fill(0)
        for (let i = 0; i < 5; i++) {
            labelsGenerated.unshift(
                moment(list?.at(-1)?.period || undefined)
                    .startOf('month')
                    .subtract(1 + i, 'month')
                    .format('YYYY-MM-DD'),
            )
        }
        for (let i = 0; i < 1; i++) {
            labelsGenerated.push(
                moment(list?.at(-1)?.period || undefined)
                    .startOf('month')
                    .subtract(0 - i, 'month')
                    .format('YYYY-MM-DD'),
            )
        }
    }
    if (list) {
        for (let i = 0; i < list.length; i++) {
            const index = labelsGenerated.findIndex((el: string) => el === list[i].period)

            if (index < 0) {
                continue
            }
            data[index] = Math.round(list[i].volume / 1000)
        }
    }
    const labels = labelsGenerated.map((el: string) => {
        const date = moment(el)
        const monthId = date.format('MM')
        const year = date.format('YYYY')
        return graphMonthWithYearPicker(monthId, year, locale)
    })
    return {
        labels,
        datasets: [
            {
                data,
                fill: true,
                backgroundColor: (context: ScriptableContext<'line'>) => {
                    const ctx = context.chart.ctx
                    const gradient = ctx.createLinearGradient(0, 0, 0, 230)
                    gradient.addColorStop(0, 'rgba(82, 210, 160,0.5)')
                    gradient.addColorStop(1, 'rgba(82, 210, 160,0.07)')
                    return gradient
                },
                borderColor: '#52D2A0',
                borderWidth: 2,
                tension: 0.5,
            },
        ],
    }
}

const Graph = ({ stats, period, smallScreen }: IGraph) => {
    const { i18n } = useTranslation()
    const locale = useMemo(() => {
        const lang = i18n.language.toLowerCase()
        return lang.includes('ru') ? 'ru' : 'en'
    }, [i18n.language])

    const sortedStats = _.sortBy(stats, (o) => o.period)
    const chartData = useMemo(
        () => generateData(sortedStats, locale, period),
        [sortedStats, locale, period],
    )
    return <Line data={chartData} options={options(stats, smallScreen)} />
}

export default Graph
